import type { LanguagePreference as AppLanguagePreference } from "~/i18n/languagePreference";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { LanguagePreference } from "~/gql/graphqlBase.generated";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useSetMyLanguagePreferenceMutation } from "~/services/endpoints/apiEndpoints";

const toGqlLanguagePreference = (preference: AppLanguagePreference): LanguagePreference =>
	preference === "ES" ? LanguagePreference.Es : LanguagePreference.En;

export interface UseLanguagePreferenceMutationsReturn {
	isSaving: boolean;
	setLanguagePreference: (preference: AppLanguagePreference) => Promise<boolean>;
}

const useLanguagePreferenceMutations = (): UseLanguagePreferenceMutationsReturn => {
	const { t } = useTranslation("common");
	const { run } = useMutationRunner();
	const [setMutation, { isLoading }] = useSetMyLanguagePreferenceMutation();

	const setLanguagePreference = useCallback(
		(preference: AppLanguagePreference): Promise<boolean> =>
			run(
				setMutation({ input: { languagePreference: toGqlLanguagePreference(preference) } }),
				(data) => data.setMyLanguagePreference,
				t("preferences.appearance.languageSaveError")
			),
		[run, setMutation, t]
	);

	return { isSaving: isLoading, setLanguagePreference };
};

export default useLanguagePreferenceMutations;
